/*
 * loksec.js -- bublina nad piny statické mapy ČR na homepage
 * (plán 11-03, komponenta `template-parts/components/loksec.php`).
 *
 * CO TENHLE MODUL NENÍ: MAPOVÁ KNIHOVNA.
 *
 * Mapa je ručně kreslené inline `<svg>` se statickými souřadnicemi
 * (zmrazený mockup `navrh-th-hp.html` ř. 939-951). OpenLayers zůstává
 * vyhrazený F4 (Lokality) podle „Pravidla mapy" v
 * `docs/component-contract-mobile-2026.md`. Tenhle modul proto nemá
 * JEDINÝ dynamický import a nestahuje ani bajt navíc. Falsifikace plánu
 * 11-03 to měří prostým grepem přes CELÝ soubor, takže se zakázaný tvar
 * (klíčové slovo `import` následované závorkou) nesmí objevit ani
 * v komentáři -- není to přehnaná přísnost: gate, který by uměl odlišit
 * komentář od kódu, jde obejít zakomentováním. Očekávaný počet je nula.
 *
 * CO MODUL DĚLÁ: nad pinem ukáže bublinu s názvem lokality a
 * podtitulkem, na kliknutí nebo Enter přejde na cíl pinu. Nic z toho
 * není nosič obsahu -- názvy měst i odkazy jsou v DOM už ze šablony
 * (odkazový seznam `.lokcities`, který je viditelný VŽDY, i pod 1024 px,
 * kde se mapa vůbec nezobrazuje). Když modul spadne nebo se nespustí,
 * neztratí se ani jedno město.
 *
 * `textContent`, NIKDY `innerHTML` (threat model 11-03, T-11-03-01).
 * Mockup skládá obsah bubliny řetězením HTML (ř. 1150); tady se místo
 * toho plní dva textové uzly. Data pocházejí z `data-` atributů, které
 * šablona vypsala přes `esc_attr()` -- druhá cesta zápisu by ten
 * escaping obešla.
 *
 * `pointerenter`/`pointerleave`, NE `mouseenter` (plán 11-03, směr D).
 * Ukazovátko na dotykových a hybridních zařízeních posílá `pointer*`
 * události; `mouse*` by na nich přišlo jen jako emulace, nebo vůbec.
 * Navíc `focus`/`blur`: pin s cílem je `role="link"` s `tabindex="0"`,
 * takže bublina musí jít vyvolat i klávesnicí -- jinak by mapa byla
 * dostupná pouze myší.
 *
 * POZICOVÁNÍ. Při ukazovátku bublina sleduje kurzor (mockup ř. 1157),
 * při fokusu se posadí nad střed pinu -- klávesnice žádnou pozici
 * kurzoru nemá a bublina uprostřed obrazovky by nedávala smysl.
 */

/** Třída na <html>: "modul mapy doběhl". */
const READY_CLASS = 'loksec-ready';

/** Třída zapnuté bubliny (mockup ř. 455). */
const ON_CLASS = 'on';

/** Odsazení bubliny od kurzoru, v pixelech (mockup ř. 1158-1159). */
const OFFSET_X = 16;
const OFFSET_Y = -14;

/**
 * Bezpečně převede NodeList na statické pole.
 *
 * @param {Element} scope Kořen hledání.
 * @param {string} selector CSS selektor.
 * @return {Array<Element>}
 */
function all(scope, selector) {
	return Array.prototype.slice.call(scope.querySelectorAll(selector));
}

/**
 * Naplní bublinu obsahem pinu. Zapisuje se výhradně `textContent`.
 *
 * @param {Element} tip Bublina.
 * @param {Element} pin Pin, nad kterým se stojí.
 * @return {void}
 */
function fill(tip, pin) {
	const name = pin.getAttribute('data-name') || '';
	const sub = pin.getAttribute('data-sub') || '';

	tip.textContent = name;

	if (sub) {
		const small = document.createElement('small');

		small.textContent = sub;
		tip.appendChild(small);
	}
}

/**
 * Posadí bublinu na souřadnice uvnitř obalu mapy.
 *
 * @param {Element} box Obal mapy (`.lokmapbig`).
 * @param {Element} tip Bublina.
 * @param {number} x Vodorovná souřadnice ve výřezu okna.
 * @param {number} y Svislá souřadnice ve výřezu okna.
 * @return {void}
 */
function place(box, tip, x, y) {
	const rect = box.getBoundingClientRect();

	tip.style.left = (x - rect.left + OFFSET_X) + 'px';
	tip.style.top = (y - rect.top + OFFSET_Y) + 'px';
}

/**
 * Skryje bublinu a vyprázdní ji, aby v DOM nezůstal text k místu, na
 * které se už neukazuje.
 *
 * @param {Element} tip Bublina.
 * @return {void}
 */
function hide(tip) {
	tip.classList.remove(ON_CLASS);
	tip.textContent = '';
}

/**
 * Ma zarizeni skutecnou hover schopnost (mys/trackpad)? Na iPhonu
 * `(hover: hover)` nesedi, hybrid (iPad s trackpadem) sedi a tam
 * jeste rozhoduje `pointerType` u kazde udalosti.
 *
 * @return {boolean}
 */
function hoverCapable() {
	try {
		return window.matchMedia('(hover: hover)').matches;
	} catch {
		return false;
	}
}

/**
 * @param {PointerEvent} event Udalost ukazovatka.
 * @return {boolean} true jen pro mys (prazdny `pointerType` = stary
 *   prohlizec bez Pointer Events, bere se jako mys).
 */
function isMousePointer(event) {
	return !event.pointerType || 'mouse' === event.pointerType;
}

/**
 * Přejde na cíl pinu, pokud ho pin má.
 *
 * @param {Element} pin Pin.
 * @return {void}
 */
function follow(pin) {
	const url = pin.getAttribute('data-url');

	if (!url) {
		return;
	}

	window.location.assign(url);
}

/**
 * Inicializace modulu. Bez mapy (pod 1024 px se `.lokmapbig` skrývá
 * CSS, bez dat se nevykreslí vůbec) se nic neděje.
 *
 * @return {void}
 */
export function init() {
	const box = document.querySelector('.lokmapbig');

	if (!box) {
		return;
	}

	const tip = box.querySelector('.maptip');

	if (!tip) {
		return;
	}

	const pins = all(box, '.pin');

	if (!pins.length) {
		return;
	}

	/*
	 * 1.27.13: bublina jen na zarizeni s myzi. iOS pri tapu syntetizuje
	 * `pointerenter`/`pointermove` PRED `click`; kdyz handler v te fazi
	 * zmeni DOM (trida `on`, `style.left/top`), WebKit to vezme jako
	 * zmenu obsahu a click zahodi (druhy tap). Na dotyku se proto
	 * handlery bubliny vubec neregistruji a pin jde rovnou na `click`.
	 */
	const hoverable = hoverCapable();

	pins.forEach(function (pin) {
		if (hoverable) {
			pin.addEventListener('pointerenter', function (event) {
				if (!isMousePointer(event)) {
					return;
				}

				fill(tip, pin);
				tip.classList.add(ON_CLASS);
			});

			pin.addEventListener('pointerleave', function () {
				hide(tip);
			});
		}

		pin.addEventListener('focus', function () {
			const rect = pin.getBoundingClientRect();

			fill(tip, pin);
			place(box, tip, rect.left + (rect.width / 2), rect.top);
			tip.classList.add(ON_CLASS);
		});

		pin.addEventListener('blur', function () {
			hide(tip);
		});

		pin.addEventListener('click', function () {
			follow(pin);
		});

		pin.addEventListener('keydown', function (event) {
			if (event.key !== 'Enter' && event.key !== ' ') {
				return;
			}

			event.preventDefault();
			follow(pin);
		});
	});

	if (hoverable) {
		box.addEventListener('pointermove', function (event) {
			if (!isMousePointer(event)) {
				return;
			}

			place(box, tip, event.clientX, event.clientY);
		});
	}

	document.documentElement.classList.add(READY_CLASS);
}
